/**
 * Data Share — privacy redaction. What leaves the device is the REDACTED copy, never the original.
 *
 * Modelled on DataClaw (github.com/peteromallet/dataclaw, MIT): its anonymizer.py / secrets.py
 * layers, re-implemented here for the browser:
 *
 *   1. Usernames   — names typed by the visitor, plus any found in home-folder paths
 *                    (C:\Users\<name>, /Users/<name>, /home/<name>), are replaced with a STABLE hash
 *                    (same name, same token everywhere), so a conversation still reads coherently.
 *   2. Secrets     — known key formats: Anthropic, OpenAI, Google, GitHub, AWS, Slack, Stripe,
 *                    Hugging Face, JWTs, private keys, passwords in connection strings, Discord webhooks.
 *   3. Entropy     — long random-looking strings in quotes are treated as possible secrets.
 *   4. Emails, phone numbers, card numbers (Luhn-checked) and IBANs.
 *   5. Custom      — any words the visitor asks us to always remove (company, client, domain...).
 *   6. Structured  — values of secret-named fields (API_KEY_BACKUP=..., "password": "...") are removed
 *                    whatever they look like, in message text, tool inputs and outputs, names and titles.
 *
 * Like DataClaw says of itself: this is NOT foolproof. It catches the common cases; the visitor sees
 * the redacted copy before anything is shared and can add their own words.
 */

import type { ShareConversation, ShareMessage } from "./types";

export type PrivacyType =
  | "username"
  | "secret"
  | "entropy"
  | "secret_field"
  | "email"
  | "phone"
  | "card"
  | "iban"
  | "custom";

export const PRIVACY_LABEL: Record<PrivacyType, string> = {
  username: "Usernames",
  secret: "Keys and tokens",
  entropy: "Possible secrets (random-looking text)",
  secret_field: "Secret-named fields",
  email: "Email addresses",
  phone: "Phone numbers",
  card: "Card numbers",
  iban: "Bank accounts",
  custom: "Your own words",
};

export type PrivacyItem = {
  type: PrivacyType;
  label: string;
  /** Index into conversation.messages, or -1 for the title. */
  message: number;
  /** Masked preview, safe to show. */
  masked: string;
};

export type PrivacyReport = {
  total: number;
  byType: Partial<Record<PrivacyType, number>>;
  items: PrivacyItem[];
  /** Usernames found automatically in file paths (shown so the visitor knows what was hidden). */
  detectedUsernames: string[];
};

export type RedactOptions = {
  usernames?: string[];
  custom?: string[];
  includeThinking?: boolean;
  includeTools?: boolean;
};

const ITEM_CAP = 200;

/* ───────────────────────────── patterns ───────────────────────────── */

export const SECRET_PATTERNS: [string, RegExp][] = [
  ["Anthropic API key", /\bsk-ant-(?:api|admin)\d{2}-[A-Za-z0-9_-]{20,}/g],
  ["OpenAI API key", /\bsk-(?!ant-)(?:proj-|svcacct-|admin-)?[A-Za-z0-9_-]{32,}/g],
  ["Google API key", /\bAIza[0-9A-Za-z_-]{35}\b/g],
  ["GitHub token", /\b(?:ghp|gho|ghu|ghs|ghr)_[A-Za-z0-9]{36,}\b|\bgithub_pat_[A-Za-z0-9_]{60,}\b/g],
  ["AWS access key", /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g],
  ["Slack token", /\bxox[abposr]-[A-Za-z0-9-]{10,}/g],
  ["Stripe key", /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{20,}/g],
  ["Hugging Face token", /\bhf_[A-Za-z]{34,}\b/g],
  ["JSON Web Token", /\beyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g],
  ["Private key", /-----BEGIN (?:RSA |EC |DSA |OPENSSH |PGP )?PRIVATE KEY(?: BLOCK)?-----[\s\S]*?(?:-----END (?:RSA |EC |DSA |OPENSSH |PGP )?PRIVATE KEY(?: BLOCK)?-----|$)/g],
  ["Discord webhook", /https:\/\/(?:ptb\.|canary\.)?discord(?:app)?\.com\/api\/webhooks\/\d+\/[A-Za-z0-9_-]{30,}/g],
];

/** user:password@ inside a URL: only the password is removed. */
const CONN_PASSWORD = /\b([a-z][a-z0-9+.-]{1,20}:\/\/[^\s:/@]{1,64}:)([^\s:/@]{3,128})(@[^\s/]+)/gi;

const SECRET_NAME = "(?:[A-Za-z0-9_.-]*?(?:api[_-]?key|apikey|secret|token|passw(?:or)?d|pwd|private[_-]?key|access[_-]?key|client[_-]?secret|auth|credential|bearer|session[_-]?id|cookie)[A-Za-z0-9_.-]*)";
/** "password": "x"  |  'api_key': 'x'  |  API_KEY_BACKUP=x  |  token: x */
const SECRET_FIELD_JSON = new RegExp(`(["']${SECRET_NAME}["']\\s*:\\s*["'])([^"'\\n]{4,})(["'])`, "gi");
const SECRET_FIELD_ENV = new RegExp(`(^|[\\s;,{(]|export\\s+)(${SECRET_NAME})(\\s*[=:]\\s*)(?!\\[REDACTED)(["']?)([^\\s"',;}{)]{6,})\\4`, "gim");
const BEARER = /\b(Bearer\s+)([A-Za-z0-9._~+/-]{16,}=*)/g;

const EMAIL = /\b[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}\b/g;
const PHONE = /(?<![\w.+])\+\d{1,3}[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d{2,4}){2,4}(?![\w.])/g;
const CARD = /(?<![\d.])[3-6]\d{3}(?:[ -]?\d{4}){2}[ -]?\d{3,4}(?![\d.])/g;
const IBAN = /\b[A-Z]{2}\d{2}(?: ?[A-Z0-9]{4}){3,7}(?: ?[A-Z0-9]{1,3})?\b/g;
const QUOTED = /(["'`])([A-Za-z0-9+/_=.-]{24,200})\1/g;
const HOME_PATH = /(?:[A-Za-z]:\\+(?:Users|Documents and Settings)\\+|\/Users\/|\/home\/)([A-Za-z0-9._ -]{2,40}?)(?=[\\/]|$|["'\s])/g;

const GENERIC_HOME = new Set(["public", "default", "shared", "all users", "user", "username", "runner", "ubuntu", "root", "admin", "guest", "you", "me", "name", "your-name", "yourname", "<user>", "..."]);
const PLACEHOLDER_EMAIL = /@(?:example|test|domain|email|company|acme)\.(?:com|org|net)$/i;

function luhn(s: string): boolean {
  const d = s.replace(/\D/g, "");
  if (d.length < 13 || d.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = d.charCodeAt(i) - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** Shannon entropy in bits per character. */
function entropy(s: string): number {
  const f = new Map<string, number>();
  for (const c of s) f.set(c, (f.get(c) || 0) + 1);
  let h = 0;
  for (const n of f.values()) {
    const p = n / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

function looksSecret(s: string): boolean {
  if (s.length < 24) return false;
  if (/^[a-z]+$/i.test(s) || /^\d+$/.test(s)) return false;
  if (/^[0-9a-f]{40}$|^[0-9a-f]{64}$/i.test(s)) return false; // git / sha256 hashes: not secret
  if (/^(?:https?|file):/.test(s) || /\.(?:js|ts|py|json|png|jpg|md|html|css)$/i.test(s)) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return false; // UUID
  const classes = [/[a-z]/, /[A-Z]/, /\d/].filter((r) => r.test(s)).length;
  return classes >= 3 && entropy(s) >= 4.2;
}

/** FNV-1a, 32 bit. Stable and synchronous, so the same name always becomes the same token. */
function stableHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function usernameToken(name: string): string {
  return `user_${stableHash(name.trim().toLowerCase())}`;
}

function mask(s: string): string {
  const t = s.trim();
  if (t.length <= 6) return "•".repeat(Math.max(3, t.length));
  return `${t.slice(0, 3)}${"•".repeat(Math.min(10, t.length - 4))}${t.slice(-1)}`;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/* ───────────────────────────── engine ───────────────────────────── */

type Ctx = {
  items: PrivacyItem[];
  byType: Partial<Record<PrivacyType, number>>;
  users: { name: string; re: RegExp; token: string }[];
  custom: { word: string; re: RegExp }[];
};

function note(ctx: Ctx, type: PrivacyType, label: string, message: number, value: string) {
  ctx.byType[type] = (ctx.byType[type] || 0) + 1;
  if (ctx.items.length < ITEM_CAP) ctx.items.push({ type, label, message, masked: mask(value) });
}

function redactText(text: string, message: number, ctx: Ctx): string {
  if (!text) return text;
  let t = text;

  // 6. Structured: secret-named fields first, so their values are gone before anything else runs.
  t = t.replace(SECRET_FIELD_JSON, (_m, a: string, v: string, b: string) => {
    if (/^\[REDACTED/.test(v) || /^(?:true|false|null|none|undefined|\*+|x+|<.*>|\$\{.*\}|your[_ -].*)$/i.test(v)) return `${a}${v}${b}`;
    note(ctx, "secret_field", "Secret-named field", message, v);
    return `${a}[REDACTED:secret]${b}`;
  });
  t = t.replace(SECRET_FIELD_ENV, (m, pre: string, key: string, eq: string, q: string, v: string) => {
    if (/^(?:true|false|null|none|undefined|\*+|x+|<.*>|\$\{?.*\}?|your[_ -].*|process\.env.*|os\.environ.*)$/i.test(v)) return m;
    note(ctx, "secret_field", `Field ${key}`, message, v);
    return `${pre}${key}${eq}${q}[REDACTED:secret]${q}`;
  });
  t = t.replace(BEARER, (_m, a: string, v: string) => {
    note(ctx, "secret", "Bearer token", message, v);
    return `${a}[REDACTED:secret]`;
  });

  // 2. Known key formats.
  for (const [label, re] of SECRET_PATTERNS) {
    re.lastIndex = 0;
    t = t.replace(re, (v) => {
      note(ctx, "secret", label, message, v);
      return "[REDACTED:secret]";
    });
  }
  t = t.replace(CONN_PASSWORD, (_m, a: string, pw: string, b: string) => {
    note(ctx, "secret", "Password in a connection string", message, pw);
    return `${a}[REDACTED:secret]${b}`;
  });

  // 3. High-entropy strings in quotes.
  t = t.replace(QUOTED, (m, q: string, v: string) => {
    if (!looksSecret(v)) return m;
    note(ctx, "entropy", "Random-looking string", message, v);
    return `${q}[REDACTED:secret]${q}`;
  });

  // 1. Usernames (typed + found in paths), with a stable token.
  for (const u of ctx.users) {
    u.re.lastIndex = 0;
    t = t.replace(u.re, (v) => {
      note(ctx, "username", "Username", message, v);
      return u.token;
    });
  }

  // 5. The visitor's own words.
  for (const c of ctx.custom) {
    c.re.lastIndex = 0;
    t = t.replace(c.re, (v) => {
      note(ctx, "custom", `"${c.word}"`, message, v);
      return "[REDACTED:custom]";
    });
  }

  // 4. Contact and money details.
  t = t.replace(EMAIL, (v) => {
    if (PLACEHOLDER_EMAIL.test(v)) return v;
    note(ctx, "email", "Email address", message, v);
    return "[REDACTED:email]";
  });
  t = t.replace(PHONE, (v) => {
    note(ctx, "phone", "Phone number", message, v);
    return "[REDACTED:phone]";
  });
  t = t.replace(CARD, (v) => {
    if (!luhn(v)) return v;
    note(ctx, "card", "Card number", message, v);
    return "[REDACTED:card]";
  });
  t = t.replace(IBAN, (v) => {
    note(ctx, "iban", "Bank account", message, v);
    return "[REDACTED:iban]";
  });

  return t;
}

/** Usernames that appear in home-folder paths anywhere in the conversation. */
export function detectUsernames(conv: ShareConversation): string[] {
  const found = new Set<string>();
  for (const m of conv.messages) {
    HOME_PATH.lastIndex = 0;
    let hit: RegExpExecArray | null;
    while ((hit = HOME_PATH.exec(m.text))) {
      const name = hit[1].trim();
      if (name && !GENERIC_HOME.has(name.toLowerCase()) && !/^[.%$<{]/.test(name)) found.add(name);
    }
  }
  return [...found];
}

export function redactConversation(
  conv: ShareConversation,
  opts: RedactOptions = {},
): { conversation: ShareConversation; report: PrivacyReport } {
  const detected = detectUsernames(conv);
  const names = [...new Set([...(opts.usernames || []), ...detected].map((s) => s.trim()).filter((s) => s.length >= 2))];
  // Longest first, so "Paul Varghese" is replaced before "Paul".
  names.sort((a, b) => b.length - a.length);
  const ctx: Ctx = {
    items: [],
    byType: {},
    users: names.map((n) => ({ name: n, re: new RegExp(`(?<![A-Za-z0-9])${escapeRe(n)}(?![A-Za-z0-9])`, "gi"), token: usernameToken(n) })),
    custom: (opts.custom || [])
      .map((w) => w.trim())
      .filter((w) => w.length >= 2)
      .map((w) => ({ word: w, re: new RegExp(escapeRe(w), "gi") })),
  };

  // Every message is kept at its own index (findings point at indexes); what is LEFT OUT of a
  // donation is decided by isIncluded(), used by the viewer and by toShareRecord().
  const messages: ShareMessage[] = [];
  conv.messages.forEach((m, i) => {
    if (!isIncluded(m, opts)) {
      messages.push({ ...m, text: "", name: m.name, attachments: undefined });
      return;
    }
    messages.push({
      ...m,
      text: redactText(m.text, i, ctx),
      name: m.name ? redactText(m.name, i, ctx) : m.name,
      attachments: m.attachments?.map((a) => ({ ...a, name: redactText(a.name, i, ctx) })),
    });
  });

  const total = Object.values(ctx.byType).reduce((a, b) => a + (b || 0), 0);
  const meta = conv.meta
    ? Object.fromEntries(Object.entries(conv.meta).map(([k, v]) => [k, redactText(v, -1, ctx)]))
    : undefined;
  return {
    conversation: { ...conv, title: redactText(conv.title, -1, ctx), meta, messages },
    report: { total, byType: ctx.byType, items: ctx.items, detectedUsernames: detected },
  };
}

export function isIncluded(m: ShareMessage, opts: RedactOptions = {}): boolean {
  if (opts.includeThinking === false && m.kind === "thinking") return false;
  if (opts.includeTools === false && (m.kind === "tool_call" || m.kind === "tool_result")) return false;
  return true;
}

/**
 * One donation record, from an ALREADY REDACTED conversation. The shape follows DataClaw's export
 * (one JSON object per conversation, messages with role / content / tool fields, model, timestamps,
 * token usage) so the two kinds of data can be read together later.
 */
export function toShareRecord(redacted: ShareConversation, opts: RedactOptions = {}) {
  return {
    schema: "askfinz.data-share/1",
    provider: redacted.provider,
    source: redacted.source,
    id: redacted.id,
    title: redacted.title,
    model: redacted.model ?? null,
    created_at: redacted.createdAt ?? null,
    updated_at: redacted.updatedAt ?? null,
    usage: redacted.usage ?? null,
    meta: redacted.meta ?? {},
    messages: redacted.messages
      .filter((m) => isIncluded(m, opts))
      .map((m) => ({
        role: m.role,
        kind: m.kind,
        content: m.text,
        ...(m.name ? { name: m.name } : {}),
        ...(m.model ? { model: m.model } : {}),
        ...(m.at ? { timestamp: m.at } : {}),
        ...(m.voice ? { voice: true } : {}),
        ...(m.isError ? { is_error: true } : {}),
        ...(m.attachments?.length ? { attachments: m.attachments } : {}),
      })),
  };
}
